import React, { useMemo } from "react";
import clsx from "clsx";

import {
  getStudentAnswersByQuestion,
  getLessonFrom,
  getIssueFrom,
  getQuestionFrom,
} from "../../../../libs/ActiveLearning";
import {
  IActiveLearningMaterialLessonsData,
  IActiveLearningStudentAnswers,
  IActiveLearningAllAnswers,
} from "../../../../state";
import styles from "../LessonsAnswerData.module.scss";

type Lesson15Pros = {
  materialCode: string;
  lessonNo: number;
  activeLearningMaterialLessons: IActiveLearningMaterialLessonsData[];
  activeLearningAnswers:
    | IActiveLearningStudentAnswers
    | IActiveLearningAllAnswers
    | null;
};

export const Lesson15: React.FC<Lesson15Pros> = ({
  materialCode,
  lessonNo,
  activeLearningMaterialLessons,
  activeLearningAnswers,
}) => {
  const activeLearningMaterialLessonsByCode = useMemo(() => {
    return activeLearningMaterialLessons.filter(
      data => data.code === materialCode,
    );
  }, [activeLearningMaterialLessons, materialCode]);

  const titleL12 = useMemo(() => {
    return getLessonFrom(activeLearningMaterialLessonsByCode, lessonNo)?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I1 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 1)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I2 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I3 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I3Q2 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 2)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I3Q3 = useMemo(() => {
    return getQuestionFrom(activeLearningMaterialLessonsByCode, lessonNo, 3, 3)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I4 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 4)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const titleL12I5 = useMemo(() => {
    return getIssueFrom(activeLearningMaterialLessonsByCode, lessonNo, 5)
      ?.title;
  }, [activeLearningMaterialLessonsByCode, lessonNo]);

  const answersL12I1Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      1,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL12I2Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      2,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL12I3Q1 = useMemo(() => {
    return getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      1,
    );
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL12I3Q2 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      2,
    );

    if (answers.length > 0) {
      const [answer] = answers;
      const parentUid = answer.contents.parentUid;
      return answersL12I3Q1.find(answer => answer.contents.uid === parentUid)
        ?.contents.value;
    } else {
      return "回答データがありません。";
    }
  }, [materialCode, activeLearningAnswers, answersL12I3Q1, lessonNo]);

  const answersL12I3Q3 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      3,
      3,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL12I4Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      4,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  const answersL12I5Q1 = useMemo(() => {
    const answers = getStudentAnswersByQuestion(
      materialCode,
      activeLearningAnswers,
      lessonNo,
      5,
      1,
    );

    return answers.length > 0
      ? answers.map(answer => answer.contents.value).join("")
      : "回答データがありません。";
  }, [materialCode, activeLearningAnswers, lessonNo]);

  return (
    <div className={styles.lesson}>
      <p className={styles.title}>
        Lesson{lessonNo < 10 ? `0${lessonNo}` : lessonNo} {titleL12}
      </p>
      <div className={styles.inner}>
        <div className={styles.work}>
          <p className={styles.no}>WORK01</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL12I1}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL12I1Q1}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK02</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL12I2}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL12I2Q1}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK03</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL12I3}</p>
            {answersL12I3Q1.length > 0 ? (
              answersL12I3Q1.map(answer => (
                <p key={answer.id} className={styles.answer}>
                  {answer.contents.value}
                </p>
              ))
            ) : (
              <p className={styles.answer}>回答データがありません。</p>
            )}
          </div>
          <div className={styles.question}>
            <div className={clsx(styles.heading, styles.dot)}>
              {titleL12I3Q2}
            </div>
            <p className={styles.answer}>{answersL12I3Q2}</p>
          </div>
          <div className={styles.question}>
            <div className={clsx(styles.heading, styles.dot)}>
              {titleL12I3Q3}
            </div>
            <p className={clsx(styles.answer, styles.high)}>{answersL12I3Q3}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK04</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL12I4}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL12I4Q1}</p>
          </div>
        </div>
        <div className={styles.work}>
          <p className={styles.no}>WORK05</p>
          <div className={styles.question}>
            <p className={styles.heading}>{titleL12I5}</p>
            <p className={clsx(styles.answer, styles.high)}>{answersL12I5Q1}</p>
          </div>
        </div>
      </div>
    </div>
  );
};
export default Lesson15;
